import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {
  AnnotationDataType,
  AnnotationType,
  CategoryType,
} from '../../../types/dataset/DatasetGetAnnotationsResponse';
import {useSelector} from 'react-redux';
import {RootState, useAppDispatch} from '../../../store';
import {
  loadAnnotations,
  mergeAnnotations,
  dataPrepareActions,
} from '../../../store/dataPrepare';
import {ConfirmDialog} from '../../../base-components/StudioConfirmDialog';
import {useIntl} from 'react-intl';
import {differenceBy, uniq} from 'lodash';
import {DetectionPrepare} from './DetectionPrepare';
import {PageListProvider} from './PageListProvider';
import {DataPreparationOrigin} from '../../../types/dataset/DataPreparationContainer';
import {BoxLabelProps} from '../../../base-components/StudioAnnotator/types';
import {MergeDialog} from '../MergeDialog';
import {ToolTypes} from '../../../base-components/StudioAnnotator/Annotator';

const {setAnnotationData} = dataPrepareActions;

type AnnotationDictionary = {
  [imageId: number]: BoxLabelProps[];
};

type DetectionViewProps = {
  projectId: string;
  inProgress: boolean;
  origin: DataPreparationOrigin;
  dataPreparationContainerId?: string;
  onBack: () => void;
  onSubmit: (annotations: AnnotationDataType) => void;
  onUpdateAnnotations: (annotations: AnnotationType[], selectedImageId: number) => void;
};

export const DetectionView = ({
  origin,
  projectId,
  inProgress,
  dataPreparationContainerId,
  onBack,
  onSubmit,
  onUpdateAnnotations,
}: DetectionViewProps) => {
  const intl = useIntl();
  const [isDetectionSubmitConfirmOpen, setIsDetectionSubmitConfirmOpen] = useState(false);
  const [isDetectionSubmitMergeOpen, setIsDetectionSubmitMergeOpen] = useState(false);
  const [processedAnnotations, setProcessedAnnotations] = useState<{
    merged: AnnotationType[];
    discarded: AnnotationType[];
    final?: AnnotationType[];
  }>({merged: [], discarded: [], final: []});
  const [activeChanges, setActiveChanges] = useState<AnnotationDictionary>({});
  const [selectedImageId, setSelectedImageId] = useState<number | undefined>(0);

  const dispatch = useAppDispatch();
  const annotationData = useSelector(
    (state: RootState) => state.dataPrepare.annotationData
  );
  const detectionAnnotations: AnnotationType[] = useMemo(
    () => annotationData?.annotations || [],
    [annotationData]
  );
  const currentCategories: CategoryType[] = useMemo(
    () => annotationData?.categories || [],
    [annotationData]
  );
  const currentCategoriesMap = currentCategories.reduce<{
    [key: string]: CategoryType;
  }>((acc, curr) => ({...acc, [curr.name]: curr}), {});
  const currentImages = annotationData?.images || [];

  useEffect(
    function loadAnnotationsData() {
      if (projectId && origin) {
        dispatch(loadAnnotations({projectId, origin}));
      }
    },
    [projectId, origin, dispatch]
  );

  const startsWithToolType = (input: string): boolean => {
    return ToolTypes.some(tool => input.startsWith(tool));
  };

  // Send changes to Backend
  const mergeActiveChanges = async (imageId: number, value: BoxLabelProps[]) => {
    if (dataPreparationContainerId && annotationData && imageId !== null) {
      const plain = value
        ?.filter(item => !!item.label)
        .map(ann => {
          const annotation = {
            id: startsWithToolType(ann.id)
              ? ((ann.id as unknown) as number)
              : (Number(ann.id as unknown) as number),
            image_id: Number(imageId),
            category_id:
              ann.label && currentCategoriesMap[ann.label]
                ? Number(currentCategoriesMap[ann.label].id)
                : undefined,
            bbox: [ann.x, ann.y, ann.width, ann.height],
          };
          return annotation;
        });

      const mergeToLocal = () => {
        const diffArr = differenceBy(annotationData.annotations, plain, 'id');
        dispatch(
          setAnnotationData({
            ...annotationData,
            annotations: diffArr.concat(plain),
          })
        );
      };

      mergeToLocal();

      await dispatch(
        mergeAnnotations({
          resourceId: dataPreparationContainerId,
          images: [{id: imageId}],
          annotations: plain,
        })
      );
    }
  };

  const onSelectionChange = useCallback(
    (imageId: number) => setSelectedImageId(imageId),
    []
  );

  const handleDetectionChange = useCallback(
    (imageId: number, rawAnnotations?: BoxLabelProps[]) => {
      if (dataPreparationContainerId && annotationData && rawAnnotations !== undefined) {
        const newAnnotations = {...activeChanges, [imageId]: rawAnnotations};
        const annotationsArray = rawAnnotations.map(boxLabelAnnotation => ({
          image_id: imageId,
          bbox: [
            boxLabelAnnotation.x,
            boxLabelAnnotation.y,
            boxLabelAnnotation.width,
            boxLabelAnnotation.height,
          ],
          category_id: boxLabelAnnotation.label
            ? currentCategories.find(({name}) => name === boxLabelAnnotation.label)?.id
            : undefined,
        }));

        setActiveChanges(newAnnotations);
        onUpdateAnnotations(annotationsArray, imageId);
      }
    },
    [
      activeChanges,
      annotationData,
      currentCategories,
      dataPreparationContainerId,
      onUpdateAnnotations,
    ]
  );

  const handleDetectionSubmit = (final: AnnotationType[]) => {
    if (
      annotationData &&
      final?.length &&
      currentCategories.length &&
      currentImages.length
    ) {
      const validAnnotations = final.filter(el => el?.category_id !== undefined);

      const validImageId = uniq(validAnnotations?.map(el => el.image_id));
      const validCategoryId = uniq(validAnnotations?.map(el => el.category_id));
      const validImages = currentImages.filter(el => validImageId.includes(el.id));
      const validCategories = currentCategories.filter(el =>
        validCategoryId.includes(el.id)
      );

      saveActiveChanges();
      onSubmit({
        ...annotationData,
        images: validImages,
        categories: validCategories,
        annotations: validAnnotations,
      });
    }
  };

  const onDone = (merged: AnnotationType[], discarded: AnnotationType[]) => {
    // Validate Annotations
    if (merged.length && merged.length !== discarded.length) {
      setProcessedAnnotations({merged, discarded});
      setIsDetectionSubmitMergeOpen(true);
    } else {
      validateLabelAndCategories(merged);
    }
  };

  const onMerge = () => {
    setIsDetectionSubmitMergeOpen(false);
    validateLabelAndCategories(processedAnnotations.merged);
  };

  const onDiscard = () => {
    setIsDetectionSubmitMergeOpen(false);
    validateLabelAndCategories(processedAnnotations.discarded);
  };

  const validateLabelAndCategories = (annotations: AnnotationType[]) => {
    setProcessedAnnotations({...processedAnnotations, final: annotations});
    const hasOrphanAnnotations = () => !annotations?.every(el => !!el.category_id);

    const hasOrphanImages = () => {
      const imagesUsedList = uniq(annotations.map(el => el.image_id));
      return imagesUsedList.length !== currentImages.length;
    };

    if (hasOrphanAnnotations() || hasOrphanImages()) {
      setIsDetectionSubmitConfirmOpen(true);
    } else {
      handleDetectionSubmit(annotations);
    }
  };

  const saveActiveChanges = () => {
    // Save unmerged changes
    const keys = Object.keys(activeChanges);
    const updatedDict = {...activeChanges};

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key !== selectedImageId?.toString()) {
        const keyNum = Number(key);
        const val = activeChanges[keyNum];

        mergeActiveChanges(keyNum, val);
        delete updatedDict[keyNum];
        setActiveChanges({...updatedDict});
      }
    }
  };

  useEffect(
    () => saveActiveChanges(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedImageId]
  );

  const isValid = Boolean(annotationData?.images?.length);
  const disableForward = !isValid || inProgress;

  const BackFromDetectionView = () => {
    saveActiveChanges();
    onBack();
  };

  return (
    <PageListProvider>
      <DetectionPrepare
        projectId={projectId}
        datasetId={dataPreparationContainerId}
        annotations={detectionAnnotations}
        labels={currentCategories}
        labelsMap={currentCategoriesMap}
        images={currentImages}
        disableForward={disableForward}
        disableBack={inProgress}
        origin={origin}
        onForward={onDone}
        onSelectionChange={onSelectionChange}
        onChange={handleDetectionChange}
        onBack={BackFromDetectionView}
      />
      <ConfirmDialog
        type="confirm"
        title={intl.formatMessage({id: 'dataPrep.detection.title'})}
        message={intl.formatMessage({id: 'dataPrep.finalize.warning.desc.detection'})}
        open={isDetectionSubmitConfirmOpen}
        onClose={() => setIsDetectionSubmitConfirmOpen(false)}
        onOk={() => {
          setIsDetectionSubmitConfirmOpen(false);
          handleDetectionSubmit(processedAnnotations.final || []);
        }}
        submitLabel={intl.formatMessage({id: 'form.confirm'})}
      />
      <MergeDialog
        open={isDetectionSubmitMergeOpen}
        title={intl.formatMessage({id: 'dataPrep.detection.title'})}
        onClose={() => setIsDetectionSubmitMergeOpen(false)}
        onMerge={onMerge}
        onDiscard={onDiscard}
      />
    </PageListProvider>
  );
};
